import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SendBird from 'sendbird';
import ChatList from './components/ChatList';
import ChatDetail from './components/ChatDetail';
import ChatEmpty from './components/ChatEmpty';
import ChatContext from './components/context/index';
import api from './components/context/api';

import { addStar, addArchive } from './components/helpers/roomaction';
import './components/styles/chatwrapper.scss';

const App = ({ ...props }) => {
  const {
    sb_id,
    sb_user,
    sb_token,
    language,
    room,
    available_language,
    portal_token,
    user_kind,
    portal_id,
  } = props;

  const [sendbirdData, setSendbirdData] = useState({
    sendbird: null,
    currentUser: null,
  });

  const [trans, setTrans] = useState(null);

  const [theRoom, setTheRoom] = useState({
    activeRoom: {},
    autoResponded: false,
    tempActiveRoom: {},
    displayRate: false,
    chatListingData: null,
    reDisplayRate: false,
    preDefinedRoom: room,
    userId: sb_user,
  });

  const [theState, setTheState] = useState({
    currentState: 'all',
    starred: '',
    archived: '',
  });

  const { sendbird, currentUser } = sendbirdData;
  const {
    activeRoom,
    autoResponded,
    userId,
    preDefinedRoom,
    chatListingData,
    displayRate,
  } = theRoom;
  const { starred, archived } = theState;

  const _addStar = (roomValue, userValue) => {
    setTheState({
      ...theState,
      starred: addStar(roomValue, userValue),
    });
  };

  const _addArchive = (roomValue, userValue) => {
    setTheState({
      ...theState,
      archived: addArchive(roomValue, userValue),
    });
  };

  const _doPing = () => {
    api.get('chat/ping/', {
      headers: {
        'x-sendbird-id': sb_user,
        'x-sendbird-token': sb_token,
      },
    });
  };

  useEffect(() => {
    const sb = new SendBird({
      appId: sb_id,
    });

    let currentLanguage = '';
    if (language !== 'en') {
      currentLanguage = `/${language}`;
    }

    fetch(`${process.env.REACT_APP_PORTAL_URL}${currentLanguage}/chat/essentials/`)
      .then(response => response.json())
      .then(data => {
        setTrans(data);
      });

    sb.connect(sb_user, sb_token, user => {
      setSendbirdData({
        sendbird: sb,
        currentUser: user,
      });
    });

    if (typeof portal_token !== 'undefined') {
      _doPing();
      setInterval(_doPing, 60000);
    }

    let disconnectTimeOut = null;

    const onFocus = () => {
      if (disconnectTimeOut !== null) {
        clearTimeout(disconnectTimeOut);
        disconnectTimeOut = null;
      }
      sb.connect(sb_user, sb_token, user => {
        setSendbirdData({
          sendbird: sb,
          currentUser: user,
        });
      });
    };

    const onBlur = () => {
      disconnectTimeOut = setTimeout(() => {
        setTheRoom({
          ...theRoom,
          activeRoom: {},
          preDefinedRoom: {},
        });
        sb.disconnect();
      }, 600000);
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    // Specify how to clean up after this effect:
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      clearTimeout(disconnectTimeOut);
      sb.disconnect();
    };
  }, []);

  const _chatItemClick = (activeRoomValue, displayRateValue = false) => {
    if (activeRoom.url !== activeRoomValue.url) {
      const isOldChat = activeRoomValue.data !== '';
      const roomInfo = isOldChat ? JSON.parse(activeRoomValue.data) : {};
      if (isOldChat) {
        api.get(`/listing/${roomInfo.id}`).then(response => {
          const getData = {
            room_code: roomInfo.chatroom_id,
          };
          api
            .get('chat/reply-auto-respond/', {
              params: getData,
              headers: {
                'x-sendbird-id': sb_user,
                'x-sendbird-token': sb_token,
              },
            })
            .then(response2 => {
              setTheRoom({
                ...theRoom,
                chatListingData: response.data,
                activeRoom: activeRoomValue,
                displayRate: displayRateValue,
                autoResponded: response2.data,
              });
              activeRoomValue.markAsRead();
            });
        });
      } else {
        setTheRoom({
          ...theRoom,
          chatListingData: {},
          activeRoom: activeRoomValue,
          displayRate: displayRateValue,
        });
        activeRoomValue.markAsRead();
      }
    } else if (displayRateValue) {
      setTheRoom({
        ...theRoom,
        displayRate: true,
      });
    }
  };

  const _closeRate = () => {
    setTheRoom({
      ...theRoom,
      displayRate: false,
    });
  };

  const contextValue = {
    sb: sendbird,
    currentUser,
    activeRoom,
    autoResponded,
    preDefinedRoom,
    activeLang: language,
    availableLang: available_language,
    userId: sb_user,
    userToken: sb_token,
    userKind: user_kind,
    portalId: portal_id,
    trans,
  };

  if (currentUser !== null && trans !== null) {
    return (
      <ChatContext.Provider value={contextValue}>
        <div className="chat-wrapper">
          <ChatList
            chatItemClick={_chatItemClick}
            addStar={_addStar}
            addArchive={_addArchive}
            starred={starred}
            archived={archived}
          />
          {Object.keys(activeRoom).length === 0 ? (
            <ChatEmpty trans={trans} />
          ) : (
            <ChatDetail
              activeRoom={activeRoom}
              displayRate={displayRate}
              chatListingData={chatListingData}
              closeRate={_closeRate}
              addStar={_addStar}
              addArchive={_addArchive}
              autoResponded={autoResponded}
              userId={userId}
            />
          )}
        </div>
      </ChatContext.Provider>
    );
  }
  return null;
};

App.defaultProps = {
  room: null,
  portal_token: null,
};

App.propTypes = {
  sb_id: PropTypes.string.isRequired,
  sb_user: PropTypes.string.isRequired,
  sb_token: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  room: PropTypes.string,
  available_language: PropTypes.string.isRequired,
  portal_token: PropTypes.string,
  user_kind: PropTypes.string.isRequired,
  portal_id: PropTypes.string.isRequired,
};

export default App;
